import React from 'react';

const TERMS_TEXT = `AXIS — Terms of Service

Operated by Introspection · Last updated: July 11, 2026

1. Introduction and Acceptance

These Terms of Service (the "Terms") govern your access to and use of Axis, a web, desktop, and mobile application operated by Introspection ("we," "us," or "our"). By creating an account or using Axis, you confirm that you have read, understood, and agree to these Terms. If you do not agree, please do not use Axis. This is a legally binding agreement between you and Introspection.

2. What Axis Is — and What It Is Not

Axis is a tool for personal self-exploration and general well-being. It helps you map and reflect on your inner world through self-assessment scans, behaviour tracking, relational maps, dream journaling, and reflective frameworks.

Axis is not a medical device, and it does not provide therapy, diagnosis, treatment, or medical advice. The scores, indicators, models, and frameworks Axis provides are personal indicators for self-reflection only. They are not a clinical measurement or a diagnosis, and they do not replace a qualified health professional. Axis is not a health service or an emergency service. If you are in distress or in crisis, please contact the appropriate support resources in your region.

3. Eligibility

You must be at least 18 years old to create an account and use Axis. By using Axis, you confirm that you are 18 or older. Axis is not intended for, and we do not knowingly permit its use by, anyone under 18.

4. Personal Use and the Professional Version

The standard version of Axis is intended for your own personal, private use. Content you enter is stored for you and kept private. We treat it as confidential and do not sell it, use it for advertising, or use it to train models; we access it only where necessary to operate, maintain, secure, or support the service, or where the law requires it (see the Privacy Policy).

Axis also offers a professional version that allows a practitioner to create and manage profiles for their own clients. If you use the professional version, you act as the party responsible for the personal information you enter about your clients, and you are responsible for obtaining any consent and meeting any privacy obligations that apply to you under the laws that govern your practice.

5. Your Content

You retain all ownership rights in the content you create, enter, or store in Axis ("Your Content"). We do not claim ownership of it.

You grant us only the limited permission necessary to host, store, back up, and display Your Content to you, so that the service can function and your data can be preserved and shown back to you across your devices. We do not sell Your Content, and we do not use it to train models or share it for advertising. You are responsible for the content you enter, including any information you record about other people; you should only record information you are entitled to keep for your own personal use.

6. Subscriptions, Payments, and Cancellation

Axis offers subscription plans that provide access to enhanced features. Fees are stated in US dollars (USD) unless otherwise indicated, and all applicable fees are shown to you before you subscribe. Payments are processed by our third-party payment processor, Stripe; we do not store your full payment card details.

Subscriptions renew automatically for the period you selected (for example, monthly or annually) until you cancel. You may cancel at any time through your account settings; cancellation takes effect at the end of your current paid period. If we change our prices, we will give you advance notice, and the new price will apply only to your next billing cycle. Your statutory rights, including any right to cancel a service contract or to a refund under applicable consumer-protection law, are not affected by these Terms.

Depending on where you subscribe, your payment is handled differently. If you subscribe through our website, your payment is processed by Stripe, and cancellations and refunds are handled by us as described above. If you subscribe through a mobile app store (such as the Apple App Store or Google Play), your purchase is a transaction between you and that store: it is subject to the store's own payment, cancellation, and refund policies, and we do not process or control those payments. To cancel or request a refund for a subscription purchased through an app store, you must use that store's account settings and support channels.

7. Intellectual Property

Axis, including its software, design, text, and the selection and arrangement of its features, is owned by Introspection or its licensors and is protected by applicable intellectual-property laws. We grant you a limited, non-exclusive, non-transferable, revocable licence to use Axis for your personal use under these Terms. The "Introspection" and "Axis" names and logos are ours; please do not use them without our written permission.

Some reflective frameworks available in Axis are built on established bodies of work, credited to their authors. These frameworks are offered as educational tools for reflection; the names of their authors are cited for attribution and do not imply any affiliation or endorsement.

8. Third-Party Services

We rely on a small number of third-party providers to operate Axis, including Stripe (payments), Railway (hosting), Vercel (deployment), Resend (transactional email), and Google (sign-in). When you use Axis, limited information is processed by these providers as described in our Privacy Policy. We do not use advertising partners and do not share your content for advertising. We are not responsible for the practices of third-party services beyond our own use of them, and we encourage you to review their terms.

9. Service Provided "As Is"

Axis is provided on an "as is" and "as available" basis. We work to keep it reliable and secure, but we do not guarantee that it will always be uninterrupted or error-free. To the extent permitted by law, we disclaim implied warranties such as merchantability and fitness for a particular purpose. Nothing in this section limits any warranty or right that cannot be excluded under the law that applies to you.

10. Suspension and Termination

You may stop using Axis and close your account at any time through your account settings. We may suspend or end your access if you materially breach these Terms or use Axis unlawfully; where reasonable and lawful, we will give you notice and an opportunity to address the issue. If your account is closed, you may request access to or deletion of your data as described in the Privacy Policy.

11. Governing Law

These Terms are governed by the laws applicable in the Province of Québec and the federal laws of Canada applicable there. Nothing in these Terms deprives you of the protection of the mandatory consumer-protection rules of the province or country where you reside; those protections continue to apply to you.

12. Changes to These Terms

We may update these Terms from time to time. If a change is significant, we will give you reasonable advance notice before it takes effect. If you continue to use Axis after the change takes effect, you accept the updated Terms; if you do not agree, you may stop using Axis and close your account.

13. Contact

If you have any questions about these Terms, contact us at hello@introspection.life.`;

const PRIVACY_TEXT = `AXIS — Privacy Policy

Operated by Introspection · Last updated: July 11, 2026

This Privacy Policy explains how Introspection ("we," "us," or "our") handles personal information in connection with Axis, our web, desktop, and mobile application for personal self-exploration and well-being. We are committed to protecting your privacy and to complying with Québec's Act respecting the protection of personal information in the private sector (as modernized by Law 25) and, where it applies, the European General Data Protection Regulation (GDPR).

1. A Note on Your Private Content

Axis is designed so that the personal content you enter, your scans, behaviour logs, relational maps, dream journal, and reflections, remains your private space. This content is stored for you and kept private. We treat it as confidential: we do not sell it, we do not use it for advertising, and we do not use it to train models. We access it only where necessary to operate, maintain, secure, or support the service, or where the law requires it. We provide the tool, and you remain in control of what you record in it.

One exception applies by design: in the professional version of Axis, a practitioner creates and manages profiles for their own clients and therefore has access to the information in those profiles. In that case, the practitioner is the party responsible for that information (see section 10).

2. Information We Collect

We collect only the limited information we need to provide Axis:

- Account information: your name and email address, and authentication data if you sign in with Google.

- Payment information: if you subscribe, payment is processed by Stripe; we do not store your full card details.

Separately from the information above, you enter and store your own content in Axis, your scans, behaviour logs, relational maps, dream journal, and reflections. This self-reported emotional, behavioural, and reflective content is entered by you and kept in your private space (see section 1). Because it concerns your inner and emotional states, it is sensitive by nature, and we treat it as confidential and protect it accordingly. We do not sell it, use it for advertising, or use it to train models.

3. Why We Use Your Information and Our Legal Basis

We use your account and payment information to create and secure your account, deliver and maintain Axis, process your subscription, and communicate with you about the service. We rely on your consent and on the performance of our contract with you as our legal bases.

Because the content you enter is sensitive, we ask for your express, specific consent when you create your account, separately from your general acceptance of the Terms. You may withdraw your consent at any time by contacting us or closing your account. Under Law 25 and the GDPR, we keep a record of the consent you provide.

4. Who We Share Information With

We do not sell your personal information and we do not share it for advertising. We rely on a small number of service providers who process limited information on our behalf, under agreements requiring them to protect it:

- Stripe — payment processing;

- Railway — hosting; Vercel — deployment;

- Resend — transactional email;

- Google — sign-in.

We may also disclose information if required by law. If our business is ever involved in a transfer such as a merger or acquisition, we will require any successor to continue protecting your information under this Policy and will inform you.

5. Where Your Information Is Stored (International Transfer)

Our service providers store and process information on servers located outside Québec, including in the United States. This means your information may be transferred to and processed in other countries whose laws may differ from those of your own. Before entrusting information to these providers, we assess that they offer an adequate level of protection, and we take reasonable measures to ensure your information remains protected in accordance with this Policy, Law 25, and the GDPR.

6. How Long We Keep Your Information

We keep your account information for as long as your account is active. When you close your account or ask us to delete your data, we delete or anonymize it within a reasonable period, unless we are required by law to keep it longer. We do not keep personal information longer than necessary for the purposes described here.

7. How We Protect Your Information

We use reasonable technical and organizational security measures appropriate to the sensitivity of the information, including access controls that limit who may access it and encryption of your data at rest. We continue to strengthen these measures over time. No system can be guaranteed perfectly secure, but we work to protect your information and to maintain a register of any confidentiality incident as required by Law 25. If an incident presents a risk of serious harm, we will notify the Commission d'accès à l'information and affected individuals as required by law.

8. Your Rights

Depending on where you live, you have rights over your personal information. In all cases, you may:

- access the personal information we hold about you;

- ask us to correct inaccurate information;

- ask us to delete your information;

- withdraw your consent; and

- request a copy of your information in a portable format (data portability).

To exercise any of these rights, contact us using the details in section 11. We will respond within the time required by applicable law. If you are in the European Union, you also have the right to lodge a complaint with your local data protection authority; in Québec, you may contact the Commission d'accès à l'information.

9. Children

Axis is intended only for people aged 18 and over. We do not knowingly collect personal information from anyone under 18. If we learn that we have collected information from a person under 18, we will delete it.

10. The Professional Version

Axis offers a professional version in which a practitioner may create profiles for their own clients. When Axis is used this way, the practitioner, not Introspection, is responsible for the personal information they enter about their clients, including obtaining the necessary consent and meeting the privacy obligations that apply to their practice. Introspection remains responsible only for providing the platform securely.

11. Person in Charge of Protecting Your Information

We have designated a person responsible for the protection of personal information (the Person in Charge of the Protection of Personal Information under Law 25), who oversees our compliance and handles your requests and questions:

Ali Jeraj - Person in Charge of the Protection of Personal Information

Introspection
Email: hello@introspection.life

12. Changes to This Policy

We may update this Privacy Policy from time to time. The updated version will show a new "last updated" date, and if the change is significant we will take reasonable steps to inform you.`;

function LegalModal({ type, onClose }) {
  if (!type) return null;
  const title = type === 'terms' ? 'Terms of Service' : 'Privacy Policy';
  const content = type === 'terms' ? TERMS_TEXT : PRIVACY_TEXT;

  return (
    <div onClick={onClose} style={styles.overlay}>
      <div onClick={e => e.stopPropagation()} style={styles.box}>
        <div style={styles.header}>
          <div style={styles.title}>{title}</div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>
        <div style={styles.content}>{content}</div>
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.btn}>Close</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.78)',
    zIndex: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  box: {
    background: '#162534',
    border: '1px solid rgba(142,196,224,0.3)',
    borderRadius: '4px',
    width: '100%',
    maxWidth: '720px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 0 40px rgba(0,0,0,0.6)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 28px',
    borderBottom: '1px solid rgba(142,196,224,0.15)',
  },
  title: {
    fontFamily: 'Georgia, serif',
    fontSize: '20px',
    fontWeight: '300',
    color: '#D8E6F0',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#8BAFC8',
    cursor: 'pointer',
    fontSize: '18px',
    padding: 0,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 28px',
    fontSize: '13px',
    lineHeight: 1.7,
    color: '#A0C4D8',
    whiteSpace: 'pre-wrap',
    fontFamily: 'Georgia, serif',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px 28px',
    borderTop: '1px solid rgba(142,196,224,0.15)',
  },
  btn: {
    background: 'rgba(142,196,224,0.15)',
    border: '1px solid rgba(142,196,224,0.4)',
    borderRadius: '3px',
    padding: '10px 24px',
    color: '#8EC4E0',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
};

export default LegalModal;